import { busyBlockDtoSchema, busyBlocksResponseSchema, busyResponseSchema, calendarFeedDtoSchema, calendarFeedsResponseSchema, createBusyBlockRequestSchema, createCalendarFeedRequestSchema, createReminderRequestSchema, exportStatusResponseSchema, exportTokenResponseSchema, mealTimesResponseSchema, reminderDtoSchema, remindersResponseSchema, replaceMealTimesRequestSchema, suggestedRemindersResponseSchema, upcomingRemindersResponseSchema, updateBusyBlockRequestSchema, updateCalendarFeedRequestSchema, updateReminderRequestSchema, } from '@fewerbags/schema';
const enc = encodeURIComponent;
/**
 * /api/v1/households/me/meal-times and /busy-blocks — the household routine
 * (D27). `base` is the households root (`/api/v1/households`).
 */
export function createScheduleApi(http, base) {
    const blocks = `${base}/me/busy-blocks`;
    return {
        mealTimes: async () => http.get(`${base}/me/meal-times`, { parse: mealTimesResponseSchema }),
        /** Replaces the whole week. */
        replaceMealTimes: async (input) => http.put(`${base}/me/meal-times`, replaceMealTimesRequestSchema.parse(input), {
            parse: mealTimesResponseSchema,
        }),
        busyBlocks: async () => http.get(blocks, { parse: busyBlocksResponseSchema }),
        createBusyBlock: async (input) => http.post(blocks, createBusyBlockRequestSchema.parse(input), { parse: busyBlockDtoSchema }),
        updateBusyBlock: async (id, input) => http.patch(`${blocks}/${enc(id)}`, updateBusyBlockRequestSchema.parse(input), {
            parse: busyBlockDtoSchema,
        }),
        removeBusyBlock: async (id) => http.del(`${blocks}/${enc(id)}`),
    };
}
/** /api/v1/calendar — imported iCal feeds, busy time and the export feed (D27). */
export function createCalendarApi(http, base) {
    const feed = (id) => `${base}/feeds/${enc(id)}`;
    return {
        feeds: async () => http.get(`${base}/feeds`, { parse: calendarFeedsResponseSchema }),
        /** Fetches the calendar once before saving; a bad address fails here. */
        addFeed: async (input) => http.post(`${base}/feeds`, createCalendarFeedRequestSchema.parse(input), {
            parse: calendarFeedDtoSchema,
        }),
        updateFeed: async (id, input) => http.patch(feed(id), updateCalendarFeedRequestSchema.parse(input), {
            parse: calendarFeedDtoSchema,
        }),
        removeFeed: async (id) => http.del(feed(id)),
        /** Sync one feed now; failures come back as `lastError` on the feed, not as an error. */
        syncFeed: async (id) => http.post(`${feed(id)}/sync`, undefined, { parse: calendarFeedDtoSchema }),
        /** Sync every feed whose last sync is older than 6 hours. */
        syncStale: async () => http.post(`${base}/sync`, undefined, { parse: calendarFeedsResponseSchema }),
        /** Busy time between two ISO instants (max 62 days). */
        busy: async (from, to, includeAllDay = false) => http.get(`${base}/busy`, {
            query: { from, to, includeAllDay: String(includeAllDay) },
            parse: busyResponseSchema,
        }),
        exportStatus: async () => http.get(`${base}/export-token`, { parse: exportStatusResponseSchema }),
        /** Create or rotate the secret export URL; the full URL is only returned here. */
        createExportToken: async () => http.post(`${base}/export-token`, undefined, { parse: exportTokenResponseSchema }),
        disableExport: async () => http.del(`${base}/export-token`),
    };
}
/** Meal-plan reminders (D28). `base` is the API root (`/api/v1`). */
export function createRemindersApi(http, base) {
    const plan = (planId) => `${base}/plans/${enc(planId)}/reminders`;
    return {
        list: async (planId) => http.get(plan(planId), { parse: remindersResponseSchema }),
        suggested: async (planId) => http.get(`${plan(planId)}/suggested`, { parse: suggestedRemindersResponseSchema }),
        create: async (planId, input) => http.post(plan(planId), createReminderRequestSchema.parse(input), {
            parse: reminderDtoSchema,
        }),
        update: async (planId, reminderId, input) => http.patch(`${plan(planId)}/${enc(reminderId)}`, updateReminderRequestSchema.parse(input), {
            parse: reminderDtoSchema,
        }),
        remove: async (planId, reminderId) => http.del(`${plan(planId)}/${enc(reminderId)}`),
        /** For the dashboard: due from 24 h ago through `days` ahead. */
        upcoming: async (days = 2) => http.get(`${base}/reminders/upcoming`, {
            query: { days },
            parse: upcomingRemindersResponseSchema,
        }),
    };
}
//# sourceMappingURL=calendar.js.map